import { tryCatchSync } from '@/try-catch';
import { isAxiosError } from 'axios';
import type { ChatSurface } from '@/features/chat/types';
import type { UploadCapabilities } from '@/features/documents/types';
import { documentExtensions, hasExtension } from '@/features/documents/utils';

const ATTACHMENT_ID_FRAGMENT = 'attachmentId=';

export const withAttachmentIdFragment = (url: string, attachmentId: string) =>
  `${url}#${ATTACHMENT_ID_FRAGMENT}${attachmentId}`;

export const parseAttachmentIdFragment = (url: string): string | null => {
  const hashIndex = url.indexOf('#');
  if (hashIndex === -1) return null;
  const fragment = url.slice(hashIndex + 1);
  return fragment.startsWith(ATTACHMENT_ID_FRAGMENT)
    ? fragment.slice(ATTACHMENT_ID_FRAGMENT.length) || null
    : null;
};

const fileExtension = (name: string) => name.slice(name.lastIndexOf('.') + 1).toLowerCase();

const IMAGE_FILE_EXTENSIONS = ['png', 'jpg', 'jpeg', 'gif', 'webp', 'avif', 'heic', 'heif', 'bmp'];

export const isImageFilename = (name: string) =>
  IMAGE_FILE_EXTENSIONS.includes(fileExtension(name));

export const isImageFile = (file: File): boolean =>
  file.type.startsWith('image/') || (!file.type && isImageFilename(file.name));

export const uploadErrorStatus = (err: unknown): 400 | 413 | 415 | 422 | 503 => {
  if (!isAxiosError(err)) return 503;
  const status = err.response?.status;
  const detail = (err.response?.data as { detail?: unknown } | undefined)?.detail;
  if (status === 422 && typeof detail === 'string' && detail.toLowerCase().includes('malware'))
    return 422;
  if (status === 400 || status === 422) return 400;
  if (status === 413 || status === 415) return status;
  return 503;
};

export const chatAttachmentSurface = (surface: ChatSurface) => {
  if (surface === 'bid_comparison') return 'tender';
  if (surface === 'contractual_supplement') return 'contract_supplement';
  return surface;
};

const IMAGE_TOKEN_RE = /\[Image:\s*([^\]\s]+)\]/g;

export const imageQueryToken = (url: string) => `[Image: ${url}]`;

export const splitImageTokens = (text: string): { text: string; imageUrls: string[] } => {
  const imageUrls: string[] = [];
  const stripped = text
    .replace(IMAGE_TOKEN_RE, (_match, url: string) => {
      imageUrls.push(url);
      return '';
    })
    .trim();
  return { text: stripped, imageUrls };
};

export const stripImageTokens = (text: string) => text.replace(/\[Image:[^\]]*\]?/g, '').trim();

export const attachmentFilenameFromUrl = (url: string): string =>
  tryCatchSync(() =>
    decodeURIComponent(new URL(url).pathname.split('/').pop() ?? ''),
  ).data?.replace(/^[0-9a-f]{32}-/, '') || 'image';

export const chatAccept = (policy: UploadCapabilities) =>
  [
    ...documentExtensions(policy.chat.documents),
    ...policy.chat.images.contentTypes,
    ...policy.chat.archives.extensions,
  ].join(',');

export const chatFilePolicy = (file: File, policy: UploadCapabilities) => {
  const { archives, documents, images } = policy.chat;
  if (hasExtension(file.name, archives.extensions)) {
    if (file.type && !archives.browserContentTypes.includes(file.type.toLowerCase())) return null;
    return {
      kind: 'archive',
      maxBytes: archives.blockMaxBytes,
      filenameMaxCharacters: archives.filenameMaxCharacters,
    };
  }
  if (hasExtension(file.name, documentExtensions(documents)))
    return { kind: 'document', maxBytes: documents.maxBytes };
  if (images.contentTypes.includes(file.type.toLowerCase()))
    return { kind: 'image', maxBytes: images.maxBytes };
  return null;
};

export const chatFileRefusal = (file: File, policy: UploadCapabilities): 400 | 413 | 415 | null => {
  const admission = chatFilePolicy(file, policy);
  if (!admission) return 415;
  if (
    !file.size ||
    (admission.filenameMaxCharacters !== undefined &&
      Array.from(file.name).length > admission.filenameMaxCharacters)
  )
    return 400;
  return file.size > admission.maxBytes ? 413 : null;
};
